import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const PluginCollaboratorsDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PluginCollaborators"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"idPlugin"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"plugin"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"idPlugin"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"collaborators"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"avatarUrl"}},{"kind":"Field","name":{"kind":"Name","value":"fullName"}},{"kind":"Field","name":{"kind":"Name","value":"idOrganizations"}},{"kind":"Field","name":{"kind":"Name","value":"initials"}},{"kind":"Field","name":{"kind":"Name","value":"username"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"PluginCollaborators","document":PluginCollaboratorsDocument}} as const;
export type PluginCollaboratorsQueryVariables = Types.Exact<{
  idPlugin: Types.Scalars['ID']['input'];
}>;


export type PluginCollaboratorsQuery = (
  { __typename: 'Query' }
  & { plugin?: Types.Maybe<(
    { __typename: 'Plugin' }
    & Pick<Types.Plugin, 'id'>
    & { collaborators: Array<(
      { __typename: 'Member' }
      & Pick<Types.Member, 'id' | 'avatarUrl' | 'fullName' | 'idOrganizations' | 'initials' | 'username'>
    )> }
  )> }
);

/**
 * __usePluginCollaboratorsQuery__
 *
 * To run a query within a React component, call `usePluginCollaboratorsQuery` and pass it any options that fit your needs.
 * When your component renders, `usePluginCollaboratorsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePluginCollaboratorsQuery({
 *   variables: {
 *      idPlugin: // value for 'idPlugin'
 *   },
 * });
 */
export function usePluginCollaboratorsQuery(
  baseOptions: TrelloQueryHookOptions<
    PluginCollaboratorsQuery,
    PluginCollaboratorsQueryVariables
  > &
    (
      | { variables: PluginCollaboratorsQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: PluginCollaboratorsDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    PluginCollaboratorsQuery,
    PluginCollaboratorsQueryVariables
  >(PluginCollaboratorsDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function usePluginCollaboratorsLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    PluginCollaboratorsQuery,
    PluginCollaboratorsQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    PluginCollaboratorsQuery,
    PluginCollaboratorsQueryVariables
  >(PluginCollaboratorsDocument, options);
}
export function usePluginCollaboratorsSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        PluginCollaboratorsQuery,
        PluginCollaboratorsQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    PluginCollaboratorsQuery,
    PluginCollaboratorsQueryVariables
  >(PluginCollaboratorsDocument, options);
}
export type PluginCollaboratorsQueryHookResult = ReturnType<
  typeof usePluginCollaboratorsQuery
>;
export type PluginCollaboratorsLazyQueryHookResult = ReturnType<
  typeof usePluginCollaboratorsLazyQuery
>;
export type PluginCollaboratorsSuspenseQueryHookResult = ReturnType<
  typeof usePluginCollaboratorsSuspenseQuery
>;
export type PluginCollaboratorsQueryResult = Apollo.QueryResult<
  PluginCollaboratorsQuery,
  PluginCollaboratorsQueryVariables
>;
